import { useMemo } from "react";
import { IPanelStyles, Panel as OfficePanel, PanelType as OfficePanelType } from "@fluentui/react";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { IPanel, PanelType } from "@cayo/shared/api/types/schema";
import { IClosableComponent } from "@cayo/shared/components/surfaces/Panel/types";
import { FCC } from "@cayo/shared/types/react";
import Resizer from "@cayo/shared/components/common/Resizer";
import globalHooks from "../GlobalHooks";
import NavigationCommands from "./NavigationCommands";
import useResizer from "./resizer.hook";
import useTypeAnnotations from "../GlobalHooks/type-annotations.hook";

type PanelProps = IPanel & {
  hideCloseButton?: boolean | undefined;
  onRefresh?: () => void;
  object?: Entity;
  helpLink?: string;
  hideToggleVerticalTabs?: boolean;
} & IClosableComponent;

const Panel: FCC<PanelProps> = (props) => {
  const { actualType, panelWidth, showResizer, onWidthChanged, handleFullscreenToggled } =
    useResizer(props.type as PanelType, props.customWidth);

  const { userSettings, setUserSettings } = globalHooks.useUserSettings();

  const { getTypeAnnotation } = useTypeAnnotations();

  const helpLink = useMemo(() => {
    return (
      props?.helpLink ||
      (props?.object?.objectType && getTypeAnnotation(props.object.objectType)?.helpLink)
    );
  }, [props?.object?.objectType, getTypeAnnotation]);

  const closeAction = props.onClose;

  return (
    <OfficePanel
      styles={styles()}
      type={actualType}
      isOpen={props.isOpen}
      isBlocking={props.isBlocking}
      customWidth={
        actualType === OfficePanelType.custom
          ? panelWidth
            ? panelWidth.toString() + "px"
            : undefined
          : undefined
      }
      hasCloseButton={false}
      onOuterClick={(e) => e && e.stopPropagation()}
      onDismiss={() => {
        closeAction!();
      }}
      onRenderNavigation={() => {
        return (
          <NavigationCommands
            actualType={actualType}
            handleFullscreenToggled={handleFullscreenToggled}
            onClose={closeAction}
            onRefresh={props?.onRefresh}
            helpLink={helpLink}
            onToggleVerticalTabs={
              props?.hideToggleVerticalTabs
                ? undefined
                : () => setUserSettings({ verticalTabs: !userSettings?.verticalTabs })
            }
            isVerticalTabsMode={userSettings?.verticalTabs}
          />
        );
      }}
      onRenderBody={(p, renderer) => {
        if (!showResizer) {
          return renderer!(p);
        }

        return <Resizer onWidthChanged={onWidthChanged}>{renderer!(p)}</Resizer>;
      }}
      onDismissed={async () => {
        const clickAction: any = (props as any)?.onClose;
        if (clickAction) {
          return clickAction();
        }

        return Promise.resolve();
      }}
    >
      {props.children}
    </OfficePanel>
  );
};

const styles = (): Partial<IPanelStyles> => {
  return {
    root: {
      marginTop: 65,
    },
    content: {
      paddingBottom: "0px !important",
      paddingLeft: "0px !important",
      paddingRight: "0px !important",
      width: "100%",
      display: "flex",
      flexFlow: "column",
    },
    commands: { position: "relative" },
    scrollableContent: {
      display: "flex",
      overflow: "hidden",
      height: "100%",
      flexFlow: "column",
    },
  };
};

export default Panel;
